import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../shared/CustomButton";
import TextInput from "../../../shared/TextInput";
import DropdownField from "../../../shared/DropdownField";
import DatePicker from "../../../shared/DatePicker";
import { paymentMethods, cardLabels, getCardOptions, getCardCost } from "../../../constants/VisitsConstants";
import { FaCheckDouble, FaTrash } from "react-icons/fa";
import { fetchData } from "../../../helpers/fetchData";
import toast from "react-hot-toast";
import { practionerDepartmentOptions } from '../../../constants/UserConstants';
import moment from "moment";
import { Skeleton } from "primereact/skeleton";
import { handlePrintData } from "../../../utils/PrintData";
import CheckPermission from "../../../hooks/CheckPermission";

const PayCreditForm = ({
    selectedPatient,
    setOpenPayCreditForm,
    withOutSlot = false,
    state,
    onPayCreditSuccess,
    invoiceData = null,
    mode = "create",
}) => {
    const { t } = useTranslation();
    const { hasPermission } = CheckPermission();
    const [loading, setLoading] = useState(false);
    const [creditLoading, setCreditLoading] = useState(false);
    const [creditBalance, setCreditBalance] = useState(0);
    const [cardOptions, setCardOptions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const isEdit = mode === "update" && !!invoiceData;
    const payCreditDetail = invoiceData?.patient_pay_credit || {};
    const paymentDetail = Array.isArray(payCreditDetail?.payment_detail)
        ? payCreditDetail.payment_detail
        : [];
    const oldPaymentAmount = Number(payCreditDetail?.patient_pay_credit_amount || 0);
    const editableCreditBalance = Number(creditBalance || 0) + oldPaymentAmount;
    const cardPayments = paymentDetail.filter(item =>
        item?.payment_method && !["cash", "bank"].includes(String(item.payment_method).toLowerCase())
    );

    const initialValues = {
        department_id: invoiceData?.department_id || '',
        patient_id: selectedPatient?.patient_id || "",
        date: payCreditDetail?.date ? moment(payCreditDetail.date).toDate() : new Date(),
        pay_credit_amount: payCreditDetail?.patient_pay_credit_amount || "",
        payment_method: payCreditDetail?.payment_method || "card",
        cardType: cardPayments?.[0]?.payment_method || "Mada",
        cash: payCreditDetail?.payment_method === "multi_payment"
            ? paymentDetail.find(item => item?.payment_method === "cash")?.amount || 0
            : null,
        bank: payCreditDetail?.payment_method === "multi_payment"
            ? paymentDetail.find(item => item?.payment_method === "bank")?.amount || 0
            : null,
        cards: payCreditDetail?.payment_method === "multi_payment" && cardPayments.length > 0
            ? cardPayments.map(item => ({
                cardType: item.payment_method,
                amount: item.amount,
            }))
            : [{ cardType: cardPayments?.[0]?.payment_method || "Mada", amount: "" }],
        remarks: payCreditDetail?.remarks || "",
    };

    const validationSchema = Yup.object().shape({
        department_id: Yup.string().required(t("Department is required")),
        date: Yup.date()
            .required(t("Date is required"))
            .max(new Date(), t("Date cannot be in the future")),
        pay_credit_amount: Yup.number()
            .required(t("Pay credit amount is required"))
            .min(0, t("Must be positive or zero"))
            .max(editableCreditBalance, t("Cannot exceed credit balance")),
        payment_method: Yup.string().required(t("Payment method is required")),
        remarks: Yup.string(),
        cash: Yup.number().nullable().when("payment_method", {
            is: "multi_payment",
            then: (schema) => schema.required(t("Cash amount is required")).min(0, t("Must be positive or zero")),
            otherwise: (schema) => schema.nullable(),
        }),
        bank: Yup.number().nullable().when("payment_method", {
            is: "multi_payment",
            then: (schema) => schema.required(t("Bank amount is required")).min(0, t("Must be positive or zero")),
            otherwise: (schema) => schema.nullable(),
        }),
    }).test(
        "payment-total-validation",
        t("Payment methods total must equal pay credit amount"),
        function (values) {
            if (values.payment_method === "multi_payment") {
                const payCreditAmount = parseFloat(values.pay_credit_amount) || 0;
                const cash = parseFloat(values.cash) || 0;
                const bank = parseFloat(values.bank) || 0;
                const cardsTotal = values.cards?.reduce((sum, card) => sum + (parseFloat(card.amount) || 0), 0) || 0;
                const total = cash + bank + cardsTotal;

                if (Math.abs(total - payCreditAmount) > 0.05) {
                    return this.createError({
                        path: "cash",
                        message: t("Payment total must equal pay credit amount"),
                    });
                }
            }
            return true;
        }
    );

    const handleSubmit = async (values) => {
        if (!hasPermission('billing', isEdit ? 'update' : 'create')) return;
        if (values?.payment_method === "card" || values?.payment_method === "multi_payment") {
            if (values?.payment_method === "card") {
                const isValid = Array.isArray(cardOptions) && cardOptions.some(group =>
                    Array.isArray(group.options) && group.options.some(opt => opt.value === values.cardType || opt.label === values.cardType)
                );
                if (!values.cardType || !isValid) {
                    toast.error(t("Please Select Card Type"));
                    return;
                }
            } else if (values?.payment_method === "multi_payment") {
                const cardPayments = values.cards?.filter(card => Number(card.amount) > 0) || [];
                for (const card of cardPayments) {
                    const isValid = Array.isArray(cardOptions) && cardOptions.some(group =>
                        Array.isArray(group.options) && group.options.some(opt => opt.value === card.cardType || opt.label === card.cardType)
                    );
                    if (!card.cardType || !isValid) {
                        toast.error(t("Please Select Card Type for all card payments"));
                        return;
                    }
                }
            }
        }

        const payload = {
            ...(isEdit
                ? {
                    receipt_number: invoiceData?.receipt_number,
                    patient_file_no: selectedPatient?.file_no || selectedPatient?.file_number || selectedPatient?.patient_file_no || invoiceData?.patient_file_no,
                }
                : {
                    patient_name: selectedPatient?.full_name,
                    patient_id: values.patient_id,
                    patient_document_id: selectedPatient?.document_id,
                    patient_file_no: selectedPatient?.file_no,
                }),
            department_id: values.department_id,
            department_name: departments.find(d => d.value === values.department_id)?.label || "",
            patient_pay_credit: {
                patient_pay_credit_amount: Number(values.pay_credit_amount),
                payment_detail: [],
                payment_method: values.payment_method,
                remarks: values.remarks || "Credit Payment",
                date: moment(values?.date)
                    .hour(moment().hour())
                    .minute(moment().minute())
                    .second(moment().second())
                    .format("YYYY-MM-DD HH:mm:ss"),
            }
        };

        // Add payment details based on payment method
        if (values.payment_method === "multi_payment") {
            payload.patient_pay_credit.payment_detail = [
                {
                    payment_method: "cash",
                    amount: values?.cash,
                },
                {
                    payment_method: "bank",
                    amount: values?.bank,
                },
                ...(values.cards?.map((card) => ({
                    payment_method: card.cardType,
                    amount: card.amount,
                    card_cost: getCardCost(cardOptions, card.cardType, card.amount),
                })) || []),
            ];

            const totalPayment = payload.patient_pay_credit.payment_detail.reduce((acc, curr) => acc + Number(curr?.amount || 0), 0);
            if (Math.abs(totalPayment - Number(values.pay_credit_amount)) > 0.05) {
                toast.error(t("Total payment must equal pay credit amount"));
                return;
            }
        } else if (values.payment_method === "card") {
            payload.patient_pay_credit.payment_detail = [
                {
                    payment_method: values?.cardType,
                    amount: Number(values.pay_credit_amount),
                    card_cost: getCardCost(cardOptions, values?.cardType, Number(values.pay_credit_amount))
                },
            ];
        }

        setLoading(true);

        try {
            const res = await fetchData(
                isEdit ? "PUT" : "POST",
                isEdit ? "/v3/patient/creditPayment/edit" : "/v3/patient/creditPayment/pay",
                payload
            );
            if (res) {
                const remainingBalance = res?.data?.remaining_balance;
                toast.success(
                    remainingBalance !== undefined
                        ? `${res?.message || t("Credit payment updated successfully")} - ${t("Remaining Balance")}: ${Number(remainingBalance).toFixed(2)}`
                        : res?.message || (isEdit ? t("Credit payment updated successfully") : t("Credit paid successfully"))
                );

                if (!isEdit && res?.data?.receipt_number) {
                    const printRes = await fetchData("POST", "/v3/patient/creditPayment/printReceipt", {
                        receipt_number: res?.data?.receipt_number
                    });

                    if (printRes) {
                        handlePrintData(printRes, t('Credit Payment Receipt'));
                    }
                }

                if (onPayCreditSuccess) {
                    onPayCreditSuccess();
                }
                setOpenPayCreditForm(false);
            }
        } catch (err) {
            toast.error(err?.message || t("Failed to pay credit"));
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await fetchData('POST', '/v3/department/getList', {});
            if (response?.data) setDepartments(practionerDepartmentOptions(response.data));
        } catch (error) {
            console.error('Failed to fetch departments', error);
        }
    };

    const fetchPatientCredit = async () => {
        setCreditLoading(true);
        try {
            const res = await fetchData('POST', '/v3/patient/creditBalance/getByPatientId', {
                patient_id: selectedPatient?.patient_id
            });
            setCreditBalance(res?.data?.patient_credit_amount ?? 0);
        } catch (err) {
            console.log('Credit Fetch Error:', err);
            setCreditBalance(0);
        } finally {
            setCreditLoading(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
        if (selectedPatient?.patient_id) {
            fetchPatientCredit();
        }
    }, [selectedPatient]);

    useEffect(() => {
        const departmentID = invoiceData?.department_id;
        const needsCardOptions = ["card", "multi_payment"].includes(payCreditDetail?.payment_method || "card");

        if (!departmentID || !needsCardOptions) return;

        getCardOptions(setCardOptions, {
            department: {
                id: departmentID
            }
        });
    }, [invoiceData?.department_id, payCreditDetail?.payment_method]);

    return (
        <div>
            {creditLoading ? (
                <div className="w-full">
                    {/* Credit Balance Skeleton */}
                    <div className="mb-4 p-4 bg-rose-50 border border-rose-200 rounded-md">
                        <div className="flex justify-between items-center">
                            <Skeleton width="60%" height="1.5rem" />
                            <Skeleton width="25%" height="2rem" />
                        </div>
                    </div>

                    {/* Form Fields Skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Skeleton width="40%" height="1rem" className="mb-2" />
                            <Skeleton width="100%" height="2.5rem" />
                        </div>
                        <div>
                            <Skeleton width="40%" height="1rem" className="mb-2" />
                            <Skeleton width="100%" height="2.5rem" />
                        </div>
                        <div>
                            <Skeleton width="40%" height="1rem" className="mb-2" />
                            <Skeleton width="100%" height="2.5rem" />
                        </div>
                        <div>
                            <Skeleton width="40%" height="1rem" className="mb-2" />
                            <Skeleton width="100%" height="2.5rem" />
                        </div>
                        <div className="md:col-span-2">
                            <Skeleton width="30%" height="1rem" className="mb-2" />
                            <Skeleton width="100%" height="2.5rem" />
                        </div>
                    </div>

                    {/* Submit Button Skeleton */}
                    <div className="mt-4 flex justify-end">
                        <Skeleton width="150px" height="2.5rem" borderRadius="0.375rem" />
                    </div>
                </div>
            ) : (
                <>
                    <div className="mb-4 p-4 bg-rose-50 border border-rose-200 rounded-md">
                        <div className="flex justify-between items-center">
                            <span className="text-14-gray font-semibold">{t('Available Credit Balance')}:</span>
                            <span className="text-18-dark font-bold text-rose-600">{Number(creditBalance || 0).toFixed(2)}</span>
                        </div>
                    </div>

                    <Formik
                        key={isEdit ? invoiceData?.receipt_number || "edit-pay-credit" : selectedPatient?.patient_id || "create-pay-credit"}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize={isEdit}
                    >
                        {(props) => (
                            <Form className="w-full">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <DropdownField
                                        filter
                                        label={t('Select Department')}
                                        options={departments}
                                        name='department_id'
                                        optionLabel='label'
                                        optionValue='value'
                                        placeholder={t('Select Department')}
                                        className='w-full'
                                        onChange={(e) => {
                                            const value = e.value;
                                            props.setFieldValue('department_id', e.value);
                                            if (value) {
                                                const payload = {
                                                    department: {
                                                        id: value
                                                    }
                                                }
                                                getCardOptions(setCardOptions, payload)
                                            }
                                        }}
                                    />

                                    <DatePicker
                                        label={t("Date")}
                                        name="date"
                                        dateFormat="dd/mm/yy"
                                        placeholder={t("Select date")}
                                        maxDate={new Date()}
                                    />

                                    <TextInput
                                        label={t("Pay Credit Amount")}
                                        name="pay_credit_amount"
                                        placeholder="0"
                                        type="number"
                                        step="any"
                                    />

                                    <DropdownField
                                        label={t("Payment Method")}
                                        options={paymentMethods(t)}
                                        placeholder={t("Select Payment Method")}
                                        name="payment_method"
                                        onChange={(e) => {
                                            const method = e.value;
                                            const currentCardKey = props.values.cardType || "Mada";

                                            props.setFieldValue("payment_method", method);
                                            if (method === "card") {
                                                props.setValues((prev) => ({
                                                    ...prev,
                                                    payment_method: method,
                                                    cardType: currentCardKey,
                                                    cash: null,
                                                    bank: null,
                                                    cards: [],
                                                }));
                                            } else if (method === "multi_payment") {
                                                props.setValues((prev) => ({
                                                    ...prev,
                                                    payment_method: method,
                                                    cardType: prev.cardType || "Mada",
                                                    cash: prev.cash ?? 0,
                                                    bank: prev.bank ?? 0,
                                                    cards: (prev.cards && prev.cards.length > 0) ? prev.cards : [{ cardType: "Mada", amount: "" }],
                                                }));
                                            } else {
                                                props.setValues((prev) => ({
                                                    ...prev,
                                                    payment_method: method,
                                                    cardType: "",
                                                    cash: null,
                                                    bank: null,
                                                    cards: [],
                                                }));
                                            }
                                        }}
                                    />

                                    {(props?.values?.payment_method === "card" ||
                                        props?.values?.payment_method === "multi_payment") && (
                                            <DropdownField
                                                label={props?.values?.payment_method === "multi_payment" ? t("Add Card") : t("Card Type")}
                                                placeholder={t("SelectCardType")}
                                                name="cardType"
                                                value={props?.values?.payment_method === "multi_payment" ? null : props.values.cardType}
                                                options={props?.values?.payment_method === "multi_payment"
                                                    ? cardOptions.map(group => ({
                                                        ...group,
                                                        options: group?.options?.filter(opt =>
                                                            !props.values.cards?.some(card => card.cardType === opt.value)
                                                        )
                                                    })).filter(group => group.options && group.options.length > 0)
                                                    : cardOptions
                                                }
                                                onChange={(e) => {
                                                    if (props.values.payment_method === "multi_payment") {
                                                        if (e.value) {
                                                            const newCards = [...(props.values.cards || []), { cardType: e.value, amount: "" }];
                                                            props.setFieldValue('cards', newCards);
                                                            setTimeout(() => props.setFieldValue('cardType', null), 0);
                                                        }
                                                    } else {
                                                        props.setFieldValue('cardType', e.value);
                                                    }
                                                }}
                                            />
                                        )}

                                    {props?.values?.payment_method === "multi_payment" && (
                                        <>
                                            <TextInput
                                                name="cash"
                                                label={t("Cash")}
                                                placeholder={t("EnterCashAmount")}
                                                type="number"
                                                step="any"
                                            />
                                            <TextInput
                                                name="bank"
                                                label={t("Bank")}
                                                placeholder={t("EnterBankAmount")}
                                                type="number"
                                                step="any"
                                            />

                                            {props.values.cards && props.values.cards.map((card, index) => (
                                                <div key={index} className="col-span-1 flex items-end gap-2">
                                                    <div className="flex-1">
                                                        <TextInput
                                                            name={`cards.${index}.amount`}
                                                            label={cardLabels[card.cardType] || card.cardType}
                                                            placeholder={t("Amount")}
                                                            type="number"
                                                            step="any"
                                                        />
                                                    </div>
                                                    <div className="mb-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const newCards = props.values.cards.filter((_, i) => i !== index);
                                                                props.setFieldValue('cards', newCards);
                                                            }}
                                                            className="text-red-500 hover:text-red-700 p-2 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                                                            title={t("Remove")}
                                                        >
                                                            <FaTrash size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    <TextInput
                                        label={t("Remarks")}
                                        name="remarks"
                                        placeholder={t("Enter remarks")}
                                        className="md:col-span-2"
                                    />
                                </div>

                                <div className="mt-4 flex justify-end items-center">
                                    <CustomButton
                                        type="submit"
                                        icon={FaCheckDouble}
                                        label={isEdit ? t("Update Credit Payment") : t("Submit Payment")}
                                        loading={loading}
                                        buttonType="primary"
                                        disabled={editableCreditBalance === 0}
                                    />
                                </div>
                            </Form>
                        )}
                    </Formik>
                </>
            )}
        </div>
    );
};

export default PayCreditForm;
